import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Copy, 
  Search, 
  Filter,
  CheckSquare,
  Save,
  X,
  AlertTriangle,
  Link
} from 'lucide-react';
import Modal from '../modal/Modal';
import { useAuth } from '../../auth/AuthProvider';
import { useAssetInspectionStore } from '../../store/assetInspectionStore';
import { AssetChecklist } from '../../service/assetInspectionService';

export interface ChecklistTemplate {
  id: string;
  _id?: string;
  name: string;
  description: string;
  category: string;
  equipmentInventoryId: string;
  equipmentSubCategoryId?: string;
  assetType: string;  // Now required
  isActive: boolean;
  items: ChecklistTemplateItem[];
  createdBy: string;
  createdAt: string;
  updatedAt?: string;
  // Display fields (populated from backend)
  equipmentInventoryName?: string;
  equipmentSubCategoryName?: string;
}

export interface ChecklistTemplateItem {
  id: string;
  _id?: string;
  question: string;
  type: 'boolean' | 'numeric' | 'text' | 'selection';
  required: boolean;
  options?: string[];
  unit?: string;
  minValue?: number;
  maxValue?: number;
  category?: string;
  order: number;
  helpText?: string;
}

interface ChecklistManagerProps {
  onChecklistSelect?: (checklist: ChecklistTemplate) => void;
  selectedAssetType?: string;
}

const ChecklistManager: React.FC<ChecklistManagerProps> = ({
  onChecklistSelect,
  selectedAssetType
}) => {
  const { user } = useAuth();
  const {
    assetChecklists,
    assetChecklistsLoading: loading,
    fetchAssetChecklists: fetchChecklistsFromStore,
    createAssetChecklist: createChecklistInStore,
    updateAssetChecklist: updateChecklistInStore,
    deleteAssetChecklist: deleteChecklistInStore,
    // duplicateAssetChecklist: duplicateChecklistInStore, // COMMENTED OUT
    toggleAssetChecklistStatus: toggleChecklistStatus
  } = useAssetInspectionStore();

  const [filteredChecklists, setFilteredChecklists] = useState<AssetChecklist[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingChecklist, setEditingChecklist] = useState<AssetChecklist | null>(null);

  // Fetch checklists from backend
  useEffect(() => {
    fetchChecklists();
  }, []);

  useEffect(() => {
    filterChecklists();
  }, [assetChecklists, searchTerm, categoryFilter, statusFilter, selectedAssetType]);

  const fetchChecklists = async () => {
    try {
      await fetchChecklistsFromStore({ limit: 100 });
    } catch (error) {
      console.error('Error fetching checklists:', error);
    }
  };

  const filterChecklists = () => {
    if (!assetChecklists || !Array.isArray(assetChecklists) || assetChecklists.length === 0) {
      setFilteredChecklists([]);
      return;
    }

    let filtered = [...assetChecklists];

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(checklist =>
        checklist?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        checklist?.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (checklist as any)?.assetTypes?.some((type: string) => type.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Filter by category
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(checklist => checklist?.category === categoryFilter);
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(checklist => 
        statusFilter === 'active' ? checklist?.isActive : !checklist?.isActive
      );
    }

    // Filter by selected asset type
    if (selectedAssetType) {
      filtered = filtered.filter(checklist =>
        (checklist as any)?.assetTypes?.some((type: string) => 
          type?.toLowerCase().includes(selectedAssetType.toLowerCase())
        )
      );
    }

    setFilteredChecklists(filtered);
  };

  const handleCreateChecklist = () => {
    setEditingChecklist(null);
    setShowCreateModal(true);
  };

  const handleEditChecklist = (checklist: AssetChecklist) => {
    setEditingChecklist(checklist);
    setShowCreateModal(true);
  };

  // Duplicate functionality not needed
  // const handleDuplicateChecklist = async (checklist: AssetChecklist) => {
  //   try {
    //     await duplicateChecklistInStore(checklist._id || checklist.id!, undefined, userId);
  //     await fetchChecklists();
  //   } catch (error) {
  //     console.error('Error duplicating checklist:', error);
  //     alert('Failed to duplicate checklist');
  //   }
  // };

  const handleDeleteChecklist = async (checklistId: string) => {
    if (window.confirm('Are you sure you want to delete this checklist?')) {
      try {
        await deleteChecklistInStore(checklistId);
        await fetchChecklists();
      } catch (error: any) {
        console.error('Error deleting checklist:', error);
        alert(error.response?.data?.error || 'Failed to delete checklist');
      }
    }
  };

  const handleToggleStatus = async (checklistId: string) => {
    try {
      await toggleChecklistStatus(checklistId);
      await fetchChecklists();
    } catch (error) {
      console.error('Error toggling checklist status:', error);
      alert('Failed to toggle checklist status');
    }
  };

  const getUniqueCategories = () => {
    if (!assetChecklists || !Array.isArray(assetChecklists) || assetChecklists.length === 0) return [];
    const categories = assetChecklists.map(c => c?.category).filter(Boolean);
    return Array.from(new Set(categories));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const handleSaveChecklist = async (checklist: ChecklistTemplate) => {
    try {
      const userId = user?.id || (user as any)?._id || "current-user";
      
      if (editingChecklist) {
        // Update existing
        await updateChecklistInStore(editingChecklist._id || editingChecklist.id!, {
          ...checklist,
          updatedBy: userId
        } as any);
      } else {
        // Create new
        await createChecklistInStore({
          ...checklist,
          createdBy: userId
        } as any);
      }
      
      setShowCreateModal(false);
      await fetchChecklists();
    } catch (error: any) {
      console.error('Error saving checklist:', error);
      if (error.response?.status === 409) {
        const errorMessage = error.response?.data?.error || 'A checklist with the same matching criteria already exists.';
        const existingChecklistName = error.response?.data?.data?.existingChecklistName;
        
        if (existingChecklistName) {
          alert(`${errorMessage}\n\nExisting checklist: "${existingChecklistName}"`);
        } else {
          alert(errorMessage);
        }
      } else {
        alert('Failed to save checklist. Please try again.');
      }
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">
            Inspection Checklists
          </h2>
          <p className="text-gray-600 mt-1">
            Manage inspection checklists for different asset types
          </p>
        </div>
        <button
          onClick={handleCreateChecklist}
          className="flex items-center gap-2 px-4 py-2 bg-[#00A8A5] text-white rounded-lg hover:bg-[#008A87] transition-colors"
        >
          <Plus size={20} />
          Create Checklist
        </button>
      </div>

      {/* Filters */}
      <div className="mb-6 space-y-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search checklists..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2"
          >
            <option value="all">All Categories</option>
            {getUniqueCategories().map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        {selectedAssetType && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-blue-800 text-sm">
              <Filter size={16} className="inline mr-1" />
              Showing checklists for: <strong>{selectedAssetType}</strong>
            </p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredChecklists.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <CheckSquare size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">
              No Checklists Found
            </h3>
            <p className="text-gray-500 mb-4">
              {!assetChecklists || assetChecklists.length === 0 
                ? "No checklists have been created yet."
                : "No checklists match your current filters."
              }
            </p>
            <button
              onClick={handleCreateChecklist}
              className="px-4 py-2 bg-[#00A8A5] text-white rounded-lg hover:bg-[#008A87] transition-colors"
            >
              Create Your First Checklist
            </button>
          </div>
        ) : (
          filteredChecklists.map((checklist) => (
            <div
              key={checklist._id || checklist.id}
              className={`border rounded-lg p-4 hover:shadow-md transition-shadow ${
                !checklist.isActive ? 'bg-gray-50 border-gray-200' : 'border-gray-200'
              }`}
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-gray-800">{checklist.name}</h3>
                    {!checklist.isActive && (
                      <span className="px-2 py-1 bg-gray-200 text-gray-600 rounded text-xs">
                        Inactive
                      </span>
                    )}
                  </div>
                  <p className="text-gray-600 text-sm mb-2">{checklist.description}</p>
                  
                  <div className="flex flex-wrap gap-1 mb-2">
                    {(checklist as any)?.equipmentInventoryName && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                        {(checklist as any)?.equipmentInventoryName}
                      </span>
                    )}
                    {(checklist as any)?.equipmentSubCategoryName && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs font-medium">
                        {(checklist as any)?.equipmentSubCategoryName}
                      </span>
                    )}
                    {(checklist as any)?.assetType && (
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                        {(checklist as any)?.assetType}
                      </span>
                    )}
                  </div>
                  
                  <div className="text-xs text-gray-500">
                    <span className="mr-4">{checklist?.items?.length || 0} items</span>
                    <span>Created {checklist?.createdAt ? formatDate(checklist.createdAt) : 'N/A'}</span>
                  </div>
                </div>
                
                <div className="flex items-center gap-1 ml-4">
                  {onChecklistSelect && (
                    <button
                      onClick={() => onChecklistSelect(checklist as any)}
                      className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      title="Use This Checklist"
                    >
                      <Link size={16} />
                    </button>
                  )}
                  
                  <button
                    onClick={() => handleEditChecklist(checklist)}
                    className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                    title="Edit Checklist"
                  >
                    <Edit size={16} />
                  </button>
                  
                  {/*Duplicate functionality not needed */}
                  {/* <button
                    onClick={() => handleDuplicateChecklist(checklist)}
                    className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                    title="Duplicate Checklist"
                  >
                    <Copy size={16} />
                  </button> */}
                  
                  <button
                    onClick={() => handleToggleStatus(checklist._id || checklist.id!)}
                    className={`p-2 rounded transition-colors ${
                      checklist.isActive 
                        ? 'text-gray-500 hover:text-orange-600 hover:bg-orange-50' 
                        : 'text-gray-500 hover:text-green-600 hover:bg-green-50'
                    }`}
                    title={checklist.isActive ? 'Deactivate' : 'Activate'}
                  >
                    {checklist.isActive ? <AlertTriangle size={16} /> : <CheckSquare size={16} />}
                  </button>
                  
                  <button
                    onClick={() => handleDeleteChecklist(checklist._id || checklist.id!)}
                    className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                    title="Delete Checklist"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              
              <div className="bg-gray-50 rounded p-3">
                <p className="text-xs text-gray-600 mb-1">Categories:</p>
                <div className="flex flex-wrap gap-1">
                  {checklist?.items && Array.isArray(checklist.items) && checklist.items.length > 0 ? (
                    Array.from(new Set(checklist.items.map(item => item.category || 'General'))).map((category, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs"
                      >
                        {category}
                      </span>
                    ))
                  ) : (
                    <span className="text-xs text-gray-500">No categories</span>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {showCreateModal && (
        <ChecklistEditor
          checklist={editingChecklist as any}
          onSave={handleSaveChecklist}
          onCancel={() => setShowCreateModal(false)}
        />
      )}
    </div>
  );
};


interface ChecklistEditorProps {
  checklist: ChecklistTemplate | null;
  onSave: (checklist: ChecklistTemplate) => void;
  onCancel: () => void;
}

const ChecklistEditor: React.FC<ChecklistEditorProps> = ({
  checklist,
  onSave,
  onCancel
}) => {
  const { equipmentData, assetSubcategoryData, allAssetsEquipment, allAssetSubcategories } = 
    require('../../store/store').useEquipmentStore();

  const [formData, setFormData] = useState<Partial<ChecklistTemplate>>({
    name: checklist?.name || '',
    description: checklist?.description || '',
    equipmentInventoryId: checklist?.equipmentInventoryId || '',
    equipmentSubCategoryId: checklist?.equipmentSubCategoryId || '',
    assetType: checklist?.assetType || '',
    category: checklist?.category || 'Fire Safety',
    isActive: checklist?.isActive ?? true,
    items: checklist?.items || [],
    createdBy: 'Current User'
  });

  const [editingItem, setEditingItem] = useState<ChecklistTemplateItem | null>(null);
  const [showItemEditor, setShowItemEditor] = useState(false);

  useEffect(() => {
    if (!equipmentData || equipmentData.length === 0) {
      allAssetsEquipment();
    }
    if (!assetSubcategoryData || assetSubcategoryData.length === 0) {
      allAssetSubcategories();
    }
  }, []);

  const handleAddItem = () => {
    setEditingItem(null);
    setShowItemEditor(true);
  };

  const handleEditItem = (item: ChecklistTemplateItem) => {
    setEditingItem(item);
    setShowItemEditor(true);
  };

  const handleSaveItem = (item: ChecklistTemplateItem) => {
    if (editingItem) {
      setFormData(prev => ({
        ...prev,
        items: prev.items?.map(i => i.id === item.id ? item : i) || []
      }));
    } else {
      const newItem = {
        ...item,
        id: Date.now().toString(),
        order: (formData.items?.length || 0) + 1
      };
      setFormData(prev => ({
        ...prev,
        items: [...(prev.items || []), newItem]
      }));
    }
    setShowItemEditor(false);
  };

  const handleDeleteItem = (itemId: string) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items?.filter(i => i.id !== itemId) || []
    }));
  };

  const handleSave = () => {
    if (!formData.name || !formData.equipmentInventoryId || !formData.assetType) {
      alert('Please fill in all required fields (name, equipment category, asset type)');
      return;
    }

    const savedChecklist: ChecklistTemplate = {
      id: checklist?.id || '',
      name: formData.name!,
      description: formData.description!,
      equipmentInventoryId: formData.equipmentInventoryId!,
      equipmentSubCategoryId: formData.equipmentSubCategoryId,
      assetType: formData.assetType!,
      category: formData.category!,
      isActive: formData.isActive!,
      items: formData.items!,
      createdBy: formData.createdBy!,
      createdAt: checklist?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    onSave(savedChecklist);
  };

  return (
    <Modal
      isOpen={true}
      onClose={onCancel}
      modalClass="bg-white w-full max-w-3xl rounded-xl shadow-lg relative max-h-[80vh] flex flex-col"
      closeButton={false}
    >
      <div className="pt-6 px-6 flex-1 overflow-y-auto">
        <h2 className="text-xl font-semibold text-gray-800 border-b pb-2 mb-6">
          {checklist ? 'Update Checklist' : 'Create New Checklist'}
        </h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
          id="checklistForm"
          className="flex flex-col gap-6 pb-20"
        >
          {/* Checklist Name */}
          <div className="flex flex-col gap-2">
            <label htmlFor="name" className="text-sm font-medium text-gray-700">
              Checklist Name <span className="text-red-500">*</span>
            </label>
            <input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
              placeholder="Enter checklist name"
              required
            />
          </div>

          {/* Category */}
          <div className="flex flex-col gap-2">
            <label htmlFor="category" className="text-sm font-medium text-gray-700">
              Category <span className="text-red-500">*</span>
            </label>
            <select
              id="category"
              value={formData.category}
              onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
              required
            >
              <option value="Fire Safety">Fire Safety</option>
              <option value="Chemical Safety">Chemical Safety</option>
              <option value="Electrical Safety">Electrical Safety</option>
              <option value="General Safety">General Safety</option>
              <option value="Environmental">Environmental</option>
            </select>
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="description" className="text-sm font-medium text-gray-700">
              Description
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none resize-y"
              rows={3}
              placeholder="Describe the purpose and scope of this checklist (optional)"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="equipmentInventoryId" className="text-sm font-medium text-gray-700">
              Equipment Category <span className="text-red-500">*</span>
            </label>
            <select
              id="equipmentInventoryId"
              value={formData.equipmentInventoryId || ''}
              onChange={(e) => {
                setFormData(prev => ({ 
                  ...prev, 
                  equipmentInventoryId: e.target.value,
                  equipmentSubCategoryId: '' // Reset subcategory when category changes
                }));
              }}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
              required
            >
              <option value="">Select Equipment Category</option>
              {equipmentData?.map((equipment: any) => (
                <option key={equipment._id} value={equipment._id}>
                  {equipment.equipmentName}
                </option>
              ))}
            </select>
          </div>

          {/* Equipment Subcategory */}
          <div className="flex flex-col gap-2">
            <label htmlFor="equipmentSubCategoryId" className="text-sm font-medium text-gray-700">
              Equipment Subcategory
            </label>
            <select
              id="equipmentSubCategoryId"
              value={formData.equipmentSubCategoryId || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, equipmentSubCategoryId: e.target.value }))}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={!formData.equipmentInventoryId}
            >
              <option value="">Select Subcategory (Optional)</option>
              {assetSubcategoryData
                ?.filter((sub: any) => sub.categoryId === formData.equipmentInventoryId)
                .map((sub: any) => (
                  <option key={sub._id} value={sub._id}>
                    {sub.subcategoryName}
                  </option>
                ))}
            </select>
            {!formData.equipmentInventoryId && (
              <p className="text-xs text-gray-500">Select equipment category first</p>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="assetType" className="text-sm font-medium text-gray-700">
              Asset Type <span className="text-red-500">*</span>
            </label>
            <input
              id="assetType"
              type="text"
              value={formData.assetType || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, assetType: e.target.value }))}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
              placeholder="e.g., CO2, DCP, Foam, ABC"
              required
            />
            <p className="text-xs text-gray-500">Specific asset type for matching (e.g., CO2, DCP, Foam)</p>
          </div>

            <div>
              <div className="flex justify-between items-center mb-4">
                <label className="block text-sm font-medium text-gray-700">
                  Checklist Items ({formData.items?.length || 0})
                </label>
                <button
                  type="button"
                  onClick={handleAddItem}
                  className="flex items-center gap-1 px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition"
                >
                  <Plus size={16} />
                  Add Item
                </button>
              </div>
              
              <div className="border border-gray-200 rounded-lg" style={{ height: '400px' }}>
                {formData.items?.length === 0 ? (
                  <div className="p-8 text-center text-gray-500 h-full flex flex-col justify-center">
                    <CheckSquare size={48} className="mx-auto mb-4 text-gray-300" />
                    <p>No checklist items yet</p>
                    <p className="text-sm">Click "Add Item" to create your first checklist item</p>
                  </div>
                ) : (
                  <div className="h-full overflow-y-auto p-3">
                    <div className="space-y-2">
                      {formData.items?.map((item, index) => (
                        <div key={item.id} className="border border-gray-200 rounded-lg p-3 bg-white">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs text-gray-500 font-mono">#{index + 1}</span>
                                <p className="font-medium text-sm">{item.question}</p>
                              </div>
                              <div className="flex gap-2 mt-1 flex-wrap">
                                <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                                  {item.type}
                                </span>
                                {item.required && (
                                  <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs">
                                    Required
                                  </span>
                                )}
                                {item.category && (
                                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                                    {item.category}
                                  </span>
                                )}
                                {item.unit && (
                                  <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                                    Unit: {item.unit}
                                  </span>
                                )}
                              </div>
                              {item.helpText && (
                                <p className="text-xs text-gray-600 mt-1 italic">{item.helpText}</p>
                              )}
                            </div>
                            <div className="flex gap-1 ml-3 flex-shrink-0">
                              <button
                                type="button"
                                onClick={() => handleEditItem(item)}
                                className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                                title="Edit Item"
                              >
                                <Edit size={14} />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteItem(item.id)}
                                className="p-1 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                                title="Delete Item"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                className="rounded"
              />
              <span className="text-sm font-medium text-gray-700">Active</span>
            </label>
          </div>

          <div className="h-20" />
        </form>
      </div>

      <div className="flex justify-end gap-3 p-3 bg-[#5A7192] w-full rounded-b-lg sticky bottom-0">
        <button
          type="button"
          className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-teal-600 transition"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="submit"
          form="checklistForm"
          className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition"
        >
          {checklist ? 'Update' : 'Create'}
        </button>
      </div>

      {showItemEditor && (
        <ChecklistItemEditor
          item={editingItem}
          onSave={handleSaveItem}
          onCancel={() => setShowItemEditor(false)}
        />
      )}
    </Modal>
  );
};

interface ChecklistItemEditorProps {
  item: ChecklistTemplateItem | null;
  onSave: (item: ChecklistTemplateItem) => void;
  onCancel: () => void;
}

const ChecklistItemEditor: React.FC<ChecklistItemEditorProps> = ({
  item,
  onSave,
  onCancel
}) => {
  const [formData, setFormData] = useState<Partial<ChecklistTemplateItem>>({
    question: item?.question || '',
    type: item?.type || 'boolean',
    required: item?.required ?? true,
    options: item?.options || [],
    unit: item?.unit || '',
    minValue: item?.minValue,
    maxValue: item?.maxValue,
    category: item?.category || 'General',
    helpText: item?.helpText || ''
  });

  const [newOption, setNewOption] = useState('');

  const handleAddOption = () => {
    if (newOption.trim() && !formData.options?.includes(newOption.trim())) {
      setFormData(prev => ({
        ...prev,
        options: [...(prev.options || []), newOption.trim()]
      }));
      setNewOption('');
    }
  };

  const handleRemoveOption = (option: string) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options?.filter(o => o !== option) || []
    }));
  };

  const handleSave = () => {
    if (!formData.question?.trim()) {
      alert('Please enter a question');
      return;
    }

    const savedItem: ChecklistTemplateItem = {
      id: item?.id || '',
      question: formData.question!,
      type: formData.type!,
      required: formData.required!,
      options: formData.options,
      unit: formData.unit,
      minValue: formData.minValue,
      maxValue: formData.maxValue,
      category: formData.category,
      order: item?.order || 1,
      helpText: formData.helpText
    };

    onSave(savedItem);
  };

  return (
    <Modal
      isOpen={true}
      onClose={onCancel}
      modalClass="bg-white w-full max-w-3xl rounded-xl shadow-lg relative max-h-[80vh] flex flex-col"
      closeButton={false}
    >
      <div className="pt-6 px-6 flex-1 overflow-y-auto">
        <h2 className="text-xl font-semibold text-gray-800 border-b pb-2 mb-6">
          {item ? 'Update Checklist Item' : 'Add New Checklist Item'}
        </h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
          id="itemForm"
          className="flex flex-col gap-6 pb-20"
        >
          <div className="flex flex-col gap-2">
            <label htmlFor="question" className="text-sm font-medium text-gray-700">
              Question <span className="text-red-500">*</span>
            </label>
            <input
              id="question"
              type="text"
              value={formData.question}
              onChange={(e) => setFormData(prev => ({ ...prev, question: e.target.value }))}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
              placeholder="Enter the inspection question"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label htmlFor="type" className="text-sm font-medium text-gray-700">
                Type <span className="text-red-500">*</span>
              </label>
              <select
                id="type"
                value={formData.type}
                onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as any }))}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                required
              >
                <option value="boolean">Yes/No</option>
                <option value="numeric">Numeric</option>
                <option value="text">Text</option>
                <option value="selection">Selection</option>
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="category" className="text-sm font-medium text-gray-700">
                Category
              </label>
              <input
                id="category"
                type="text"
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                placeholder="e.g., Physical Inspection"
              />
            </div>
          </div>

          {formData.type === 'numeric' && (
            <div className="grid grid-cols-3 gap-4">
              <div className="flex flex-col gap-2">
                <label htmlFor="unit" className="text-sm font-medium text-gray-700">
                  Unit
                </label>
                <input
                  id="unit"
                  type="text"
                  value={formData.unit}
                  onChange={(e) => setFormData(prev => ({ ...prev, unit: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  placeholder="e.g., kg, °C"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor="minValue" className="text-sm font-medium text-gray-700">
                  Min Value
                </label>
                <input
                  id="minValue"
                  type="number"
                  value={formData.minValue || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, minValue: Number(e.target.value) }))}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  placeholder="Min"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor="maxValue" className="text-sm font-medium text-gray-700">
                  Max Value
                </label>
                <input
                  id="maxValue"
                  type="number"
                  value={formData.maxValue || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, maxValue: Number(e.target.value) }))}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  placeholder="Max"
                />
              </div>
            </div>
          )}

          {formData.type === 'selection' && (
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Options <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newOption}
                  onChange={(e) => setNewOption(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddOption();
                    }
                  }}
                  className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  placeholder="Add option"
                />
                <button
                  type="button"
                  onClick={handleAddOption}
                  className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.options?.map((option, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm flex items-center gap-1"
                  >
                    {option}
                    <button
                      type="button"
                      onClick={() => handleRemoveOption(option)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <X size={14} />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-4 items-start">
            <div className="flex flex-col gap-2 col-span-2">
              <label htmlFor="helpText" className="text-sm font-medium text-gray-700">
                Help Text
              </label>
              <textarea
                id="helpText"
                value={formData.helpText}
                onChange={(e) => setFormData(prev => ({ ...prev, helpText: e.target.value }))}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none resize-y"
                rows={2}
                placeholder="Optional instructions"
              />
            </div>

            <div className="flex flex-col gap-2 justify-end h-full">
              <label className="text-sm font-medium text-gray-700 mb-1">
                Settings
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.required}
                  onChange={(e) => setFormData(prev => ({ ...prev, required: e.target.checked }))}
                  className="rounded"
                />
                <span className="text-sm font-medium text-gray-700">Required</span>
              </label>
            </div>
          </div>

          <div className="h-20" />
        </form>
      </div>

      <div className="flex justify-end gap-3 p-3 bg-[#5A7192] w-full rounded-b-lg sticky bottom-0">
        <button
          type="button"
          className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-teal-600 transition"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="submit"
          form="itemForm"
          className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition"
        >
          {item ? 'Update' : 'Add Item'}
        </button>
      </div>
    </Modal>
  );
};

export default ChecklistManager;
